class CollectionProductGrid {
  constructor() {
    this.products = [];
    this.filteredProducts = [];
    this.currentPage = 1;
    this.itemsPerPage = 24;
    this.totalPages = 1;
    this.currentSort = '';
    this.currentFilters = {
      priceRange: { min: null, max: null },
      productType: [],
      size: [],
      material: [],
      style: [],
      thickness: [],
      wearLayer: [],
      width: [],
      length: [],
      availability: null
    };
    
    this.sortOptions = {
      '': { label: 'Sort by', field: null, direction: 'asc' },
      'featured': { label: 'Featured', field: 'featured', direction: 'desc' },
      'best-selling': { label: 'Best Selling', field: 'bestSelling', direction: 'desc' },
      'alphabetically': { label: 'A-Z', field: 'title', direction: 'asc' },
      'alphabetically-reverse': { label: 'Z-A', field: 'title', direction: 'desc' },
      'price-ascending': { label: 'Price: Low to High', field: 'sortPrice', direction: 'asc' },
      'price-descending': { label: 'Price: High to Low', field: 'sortPrice', direction: 'desc' },
      // 'date-ascending': { label: 'Date: Old to New', field: 'createdAt', direction: 'asc' },
      // 'date-descending': { label: 'Date: New to Old', field: 'createdAt', direction: 'desc' },
      // 'vendor-asc': { label: 'Vendor: A-Z', field: 'vendor', direction: 'asc' },
      // 'vendor-desc': { label: 'Vendor: Z-A', field: 'vendor', direction: 'desc' },
      // 'type-asc': { label: 'Type: A-Z', field: 'productType', direction: 'asc' },
      // 'type-desc': { label: 'Type: Z-A', field: 'productType', direction: 'desc' },
      // 'availability': { label: 'Available First', field: 'availability', direction: 'desc' }
    };

    // Preloader elements
    this.collectionPreloader = null;
    this.gridPreloader = null;

    this.init();
  }

  init() {
    this.collectProducts();
    this.setupEventListeners();
    this.populateSortDropdown();
    this.updateDisplay();
    this.parseURLParams();
    this.initializePreloaders();
  }

  // Initialize preloader elements
  initializePreloaders() {
    this.collectionPreloader = document.getElementById('collection-preloader');
    this.gridPreloader = document.getElementById('grid-preloader');
  }

  // Show full-screen preloader for major operations
  showCollectionPreloader(message = 'Loading products...') {
    if (this.collectionPreloader) {
      const textElement = this.collectionPreloader.querySelector('.collection-preloader-text');
      if (textElement) {
        textElement.textContent = message;
      }
      this.collectionPreloader.classList.add('active');
    }
  }

  // Hide full-screen preloader
  hideCollectionPreloader() {
    if (this.collectionPreloader) {
      this.collectionPreloader.classList.remove('active');
    }
  }

  // Show grid overlay preloader for lighter operations
  showGridPreloader() {
    if (this.gridPreloader) {
      this.gridPreloader.classList.add('active');
    }
  }

  // Hide grid overlay preloader
  hideGridPreloader() {
    if (this.gridPreloader) {
      this.gridPreloader.classList.remove('active');
    }
  }

  collectProducts() {
    const productElements = document.querySelectorAll('.product-item');
    this.products = Array.from(productElements).map(element => {
      const customPriceValue = element.dataset.customPrice || '';
      const sortPriceValue = parseFloat(element.dataset.sortprice) || 999999;
      const extractedPrice = this.extractDisplayPrice(customPriceValue);
      
      return {
        element,
        id: element.dataset.productId,
        title: element.dataset.title || '',
        vendor: element.dataset.vendor || '',
        productType: element.dataset.productType || '',
        collections: element.dataset.collections ? element.dataset.collections.split(',') : [],
        price: parseFloat(element.dataset.price) || 0,
        sortPrice: sortPriceValue,
        customPrice: customPriceValue,
        flooringPrice: element.dataset.flooringPrice || '',
        // Extract the actual display price from custom price for sorting
        displayPrice: extractedPrice,
        availability: element.dataset.availability === 'true',
        createdAt: element.dataset.createdAt || '',
        size: element.dataset.size || '',
        color: element.dataset.color || '',
        material: element.dataset.material || '',
        brand: element.dataset.brand || '',
        style: element.dataset.style || '',
        thickness: element.dataset.thickness || '',
        wearLayer: element.dataset.wearLayer || '',
        width: element.dataset.width || '',
        length: element.dataset.length || ''
      };
    });
    
    this.filteredProducts = [...this.products];
  }

  extractDisplayPrice(customPriceString) {
    if (!customPriceString) return 999999; // High value for products without price
    
    // Handle different price formats:
    // "From $2.99 sq ft" -> 2.99
    // "$15.50" -> 15.50
    // "$2.99 sq ft" -> 2.99
    
    // Remove "From" prefix and extract price after $
    const cleanPrice = customPriceString.replace(/from\s*/i, '').trim();
    const priceMatch = cleanPrice.match(/\$?([\d.]+)/i);
    
    return priceMatch ? parseFloat(priceMatch[1]) : 999999;
  }

  setupEventListeners() {
    // Simplified sort dropdown
    const sortDropdown = document.getElementById('sort-dropdown');
    if (sortDropdown) {
      sortDropdown.addEventListener('change', (e) => this.handleSort(e.target.value));
    }

    // Items per page in toolbar
    const toolbarItemsPerPage = document.getElementById('toolbar-items-per-page');
    if (toolbarItemsPerPage) {
      toolbarItemsPerPage.addEventListener('change', (e) => {
        this.showGridPreloader();
        this.itemsPerPage = parseInt(e.target.value);
        this.currentPage = 1;
        
        // Use setTimeout to allow preloader to show
        setTimeout(() => {
          this.updateDisplay();
          this.updateURL();
          this.hideGridPreloader();
        }, 800);
      });
    }

    // Filter panel toggle
    const filterButton = document.getElementById('search__filter__btn');
    const filterPanel = document.getElementById('custom-filter-panel');
    const filterOverlay = document.getElementById('filter-overlay');
    const closeFilterBtn = document.getElementById('close-filter-panel');

    if (filterButton && filterPanel) {
      filterButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.openFilterPanel();
      });
    }

    if (closeFilterBtn) {
      closeFilterBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.closeFilterPanel();
      });
    }

    if (filterOverlay) {
      filterOverlay.addEventListener('click', () => {
        this.closeFilterPanel();
      });
    }

    // Click outside filter panel to close
    document.addEventListener('click', (e) => {
      const filterPanel = document.getElementById('custom-filter-panel');
      const filterButton = document.getElementById('search__filter__btn');
      
      if (filterPanel && filterPanel.classList.contains('active')) {
        // Check if click is outside filter panel and not on filter button
        if (!filterPanel.contains(e.target) && !filterButton.contains(e.target)) {
          this.closeFilterPanel();
        }
      }
    });

    // Setup custom filter listeners
    this.setupCustomFilterListeners();

    // Pagination
    document.addEventListener('click', (e) => {
      if (e.target.matches('.pagination-btn[data-page]')) {
        e.preventDefault();
        const page = parseInt(e.target.dataset.page);
        if (page !== this.currentPage) {
          this.showGridPreloader();
          this.currentPage = page;
          
          // Use setTimeout to allow preloader to show
          setTimeout(() => {
            this.updateDisplay();
            this.updateURL();
            this.scrollToTop();
            this.hideGridPreloader();
          }, 800);
        }
      }
    });

    // Browser back/forward
    window.addEventListener('popstate', () => {
      this.parseURLParams();
    });
  }

  setupCustomFilterListeners() {
    // Initialize filter panel
    this.initializeFilterPanel();

    // Price filter
    const applyPriceBtn = document.getElementById('apply-price-filter');
    if (applyPriceBtn) {
      applyPriceBtn.addEventListener('click', () => {
        this.handlePriceFilter();
      });
    }

    // Clear all filters
    const clearAllBtn = document.getElementById('clear-all-filters');
    if (clearAllBtn) {
      clearAllBtn.addEventListener('click', () => {
        this.clearAllFilters();
      });
    }

    // Dynamic filter change listeners (will be added when filters are created)
    document.addEventListener('change', (e) => {
      if (e.target.matches('.filter-checkbox')) {
        this.handleFilterChange();
      }
      if (e.target.matches('.filter-select')) {
        this.handleFilterChange();
      }
    });

    // Active filter removal
    document.addEventListener('click', (e) => {
      if (e.target.matches('.active-filter-remove')) {
        e.preventDefault();
        const filterType = e.target.dataset.filterType;
        const filterValue = e.target.dataset.filterValue;
        this.removeActiveFilter(filterType, filterValue);
      }
      
      if (e.target.matches('.clear-all-active-filters')) {
        e.preventDefault();
        this.clearAllFilters();
      }
    });
  }

  openFilterPanel() {
    const filterPanel = document.getElementById('custom-filter-panel');
    const filterOverlay = document.getElementById('filter-overlay');
    
    if (filterPanel) filterPanel.classList.add('active');
    if (filterOverlay) filterOverlay.classList.add('active');
    document.body.style.overflow = 'hidden';
  }

  closeFilterPanel() {
    const filterPanel = document.getElementById('custom-filter-panel');
    const filterOverlay = document.getElementById('filter-overlay');
    
    if (filterPanel) filterPanel.classList.remove('active');
    if (filterOverlay) filterOverlay.classList.remove('active');
    document.body.style.overflow = 'auto';
  }

  initializeFilterPanel() {
    this.populatePriceFilters();
    this.createDynamicFilters();
    this.updateActiveFiltersDisplay();
  }

  populateSortDropdown() {
    const sortDropdown = document.getElementById('sort-dropdown');
    if (!sortDropdown) return;

    // Clear existing options except the first one
    const firstOption = sortDropdown.querySelector('option[value=""]');
    sortDropdown.innerHTML = '';
    if (firstOption) {
      sortDropdown.appendChild(firstOption);
    }

    // Add sort options
    Object.entries(this.sortOptions).forEach(([value, option]) => {
      if (value === '') return; // Skip the default option
      
      const optionElement = document.createElement('option');
      optionElement.value = value;
      optionElement.textContent = option.label;
      sortDropdown.appendChild(optionElement);
    });
  }

  handleSort(sortValue) {
    this.showCollectionPreloader('Sorting products...');
    this.currentSort = sortValue;
    this.currentPage = 1;
    
    // Use setTimeout to allow preloader to show before heavy operation
    setTimeout(() => {
      this.applyFiltersAndSort();
      this.updateDisplay();
      this.updateURL();
      this.hideCollectionPreloader();
    }, 800);
  }

  // Method to ensure we have the current sort from the dropdown
  ensureCurrentSort() {
    const sortDropdown = document.getElementById('sort-dropdown');
    if (sortDropdown && sortDropdown.value !== this.currentSort) {
      this.currentSort = sortDropdown.value;
    }
  }

  applyFiltersAndSort() {
    let filtered = [...this.products];

    // Apply filters
    filtered = this.applyFilters(filtered);

    // Apply sorting
    if (this.currentSort && this.sortOptions[this.currentSort]) {
      filtered = this.sortProducts(filtered, this.currentSort);
    }

    this.filteredProducts = filtered;
    this.calculatePagination();
  }

  applyFilters(products) {
    return products.filter(product => {
      // Price range filter
      if (this.currentFilters.priceRange.min !== null || this.currentFilters.priceRange.max !== null) {
        const productPrice = product.sortPrice;
        if (this.currentFilters.priceRange.min !== null && productPrice < this.currentFilters.priceRange.min) {
          return false;
        }
        if (this.currentFilters.priceRange.max !== null && productPrice > this.currentFilters.priceRange.max) {
          return false;
        }
      }

      // Product type filter
      if (this.currentFilters.productType.length > 0) {
        if (!this.currentFilters.productType.includes(product.productType)) {
          return false;
        }
      }

      // Size filter
      if (this.currentFilters.size.length > 0) {
        if (!this.currentFilters.size.includes(product.size)) {
          return false;
        }
      }

      // Material filter
      if (this.currentFilters.material.length > 0) {
        if (!this.currentFilters.material.includes(product.material)) {
          return false;
        }
      }

      // Style filter
      if (this.currentFilters.style.length > 0) {
        if (!this.currentFilters.style.includes(product.style)) {
          return false;
        }
      }

      // Thickness filter
      if (this.currentFilters.thickness.length > 0) {
        if (!this.currentFilters.thickness.includes(product.thickness)) {
          return false;
        }
      }

      // Wear Layer filter
      if (this.currentFilters.wearLayer.length > 0) {
        if (!this.currentFilters.wearLayer.includes(product.wearLayer)) {
          return false;
        }
      }

      // Width filter
      if (this.currentFilters.width.length > 0) {
        if (!this.currentFilters.width.includes(product.width)) {
          return false;
        }
      }

      // Length filter
      if (this.currentFilters.length.length > 0) {
        if (!this.currentFilters.length.includes(product.length)) {
          return false;
        }
      }

      // Availability filter
      if (this.currentFilters.availability !== null) {
        if (product.availability !== this.currentFilters.availability) {
          return false;
        }
      }

      return true;
    });
  }

  populatePriceFilters() {
    // Get unique sortPrice values from products (this is the calculated price used for sorting)
    const prices = [...new Set(this.products.map(p => p.sortPrice))]
      .filter(price => price && price < 999999 && price > 0) // Filter out invalid prices
      .sort((a, b) => a - b);

    const minSelect = document.getElementById('min-price');
    const maxSelect = document.getElementById('max-price');

    if (minSelect && maxSelect && prices.length > 0) {
      // Clear existing options (except first)
      minSelect.innerHTML = '<option value="">Any</option>';
      maxSelect.innerHTML = '<option value="">Any</option>';

      // Add individual price options instead of ranges
      prices.forEach(price => {
        const formattedPrice = `$${price.toFixed(2)}`;
        
        const minOption = new Option(formattedPrice, price);
        const maxOption = new Option(formattedPrice, price);
        
        minSelect.appendChild(minOption);
        maxSelect.appendChild(maxOption);
      });
    }
  }

  createDynamicFilters() {
    const dynamicFiltersContainer = document.getElementById('dynamic-filters');
    if (!dynamicFiltersContainer) return;

    // Arrange filters in the specified order with dropdown types (removed collection, color, brand, vendor)
    const filterConfigs = [
      { key: 'length', label: 'Length', type: 'dropdown' },
      { key: 'style', label: 'Style', type: 'dropdown' },
      { key: 'thickness', label: 'Thickness', type: 'dropdown' },
      { key: 'wearLayer', label: 'Wear Layer', type: 'dropdown' },
      { key: 'width', label: 'Width', type: 'dropdown' },
      { key: 'productType', label: 'Product Type', type: 'checkbox' },
      { key: 'size', label: 'Size', type: 'checkbox' },
      { key: 'material', label: 'Material', type: 'checkbox' }
    ];

    dynamicFiltersContainer.innerHTML = '';

    filterConfigs.forEach(config => {
      const values = this.getUniqueFilterValues(config.key);
      if (values.length > 0) {
        const filterSection = this.createFilterSection(config, values);
        dynamicFiltersContainer.appendChild(filterSection);
      }
    });
  }

  getUniqueFilterValues(key) {
    const values = new Set();
    
    this.products.forEach(product => {
      if (product[key] && product[key].trim()) {
        values.add(product[key].trim());
      }
    });

    return Array.from(values).sort();
  }

  createFilterSection(config, values) {
    const section = document.createElement('div');
    section.className = 'filter-section';
    
    const title = document.createElement('h3');
    title.textContent = config.label;
    section.appendChild(title);

    if (config.type === 'dropdown') {
      const select = document.createElement('select');
      select.className = 'filter-select';
      select.dataset.filterType = config.key;

      // Add default option
      const defaultOption = document.createElement('option');
      defaultOption.value = '';
      defaultOption.textContent = `Select ${config.label}`;
      select.appendChild(defaultOption);

      // Add value options
      values.forEach(value => {
        const option = document.createElement('option');
        option.value = value;
        option.textContent = value;
        select.appendChild(option);
      });

      section.appendChild(select);

    } else if (config.type === 'checkbox') {
      const checkboxList = document.createElement('div');
      checkboxList.className = 'filter-checkbox-list';

      values.forEach(value => {
        const item = document.createElement('div');
        item.className = 'filter-checkbox-item';

        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.className = 'filter-checkbox';
        checkbox.id = `filter-${config.key}-${value}`;
        checkbox.dataset.filterType = config.key;
        checkbox.value = value;

        const label = document.createElement('label');
        label.htmlFor = checkbox.id;
        label.textContent = value;

        item.appendChild(checkbox);
        item.appendChild(label);
        checkboxList.appendChild(item);
      });

      section.appendChild(checkboxList);
    }

    return section;
  }

  handlePriceFilter() {
    const minSelect = document.getElementById('min-price');
    const maxSelect = document.getElementById('max-price');
    
    const min = minSelect && minSelect.value ? parseFloat(minSelect.value) : null;
    const max = maxSelect && maxSelect.value ? parseFloat(maxSelect.value) : null;

    this.showCollectionPreloader('Filtering by price...');
    
    // Ensure we have the current sort state
    this.ensureCurrentSort();

    this.currentFilters.priceRange.min = min;
    this.currentFilters.priceRange.max = max;
    this.currentPage = 1;
    
    // Use setTimeout to allow preloader to show
    setTimeout(() => {
      this.applyFiltersAndSort();
      this.updateDisplay();
      this.updateActiveFiltersDisplay();
      this.updateURL();
      this.hideCollectionPreloader();
    }, 1000);
  }

  handleFilterChange() {
    this.showCollectionPreloader('Applying filters...');
    this.collectCurrentFilters();
    this.ensureCurrentSort();
    this.currentPage = 1;
    
    // Use setTimeout to allow preloader to show
    setTimeout(() => {
      this.applyFiltersAndSort();
      this.updateDisplay();
      this.updateActiveFiltersDisplay();
      this.updateURL();
      this.hideCollectionPreloader();
    }, 1000);
  }

  collectCurrentFilters() {
    // Reset array filters but keep price range
    const priceRange = this.currentFilters.priceRange;
    this.currentFilters = {
      priceRange,
      productType: [],
      size: [],
      material: [],
      style: [],
      thickness: [],
      wearLayer: [],
      width: [],
      length: [],
      availability: null
    };

    // Collect checkbox filters
    const checkboxes = document.querySelectorAll('.filter-checkbox:checked');
    checkboxes.forEach(checkbox => {
      const filterType = checkbox.dataset.filterType;
      const filterValue = checkbox.value;
      
      if (this.currentFilters[filterType]) {
        this.currentFilters[filterType].push(filterValue);
      }
    });

    // Collect dropdown filters
    const selects = document.querySelectorAll('.filter-select');
    selects.forEach(select => {
      if (select.value) {
        const filterType = select.dataset.filterType;
        const filterValue = select.value;
        
        if (this.currentFilters[filterType]) {
          this.currentFilters[filterType].push(filterValue);
        }
      }
    });
  }

  updateActiveFiltersDisplay() {
    const container = document.getElementById('active-filters');
    if (!container) return;

    const activeFilters = [];

    // Price range filter
    if (this.currentFilters.priceRange.min !== null || this.currentFilters.priceRange.max !== null) {
      const min = this.currentFilters.priceRange.min ? `$${this.currentFilters.priceRange.min.toFixed(2)}` : 'Any';
      const max = this.currentFilters.priceRange.max ? `$${this.currentFilters.priceRange.max.toFixed(2)}` : 'Any';
      activeFilters.push({
        type: 'priceRange',
        label: `Price: ${min} - ${max}`,
        value: 'priceRange'
      });
    }

    // Other filters
    Object.keys(this.currentFilters).forEach(filterType => {
      if (Array.isArray(this.currentFilters[filterType])) {
        this.currentFilters[filterType].forEach(value => {
          activeFilters.push({
            type: filterType,
            label: `${this.getFilterLabel(filterType)}: ${value}`,
            value: value
          });
        });
      }
    });

    // Render active filters
    if (activeFilters.length === 0) {
      container.innerHTML = '';
      return;
    }

    let html = '';
    activeFilters.forEach(filter => {
      html += `
        <div class="active-filter-tag">
          ${filter.label}
          <button class="active-filter-remove" 
                  data-filter-type="${filter.type}" 
                  data-filter-value="${filter.value}">×</button>
        </div>
      `;
    });

    html += '<button class="clear-all-active-filters">Clear All</button>';
    container.innerHTML = html;
  }

  getFilterLabel(filterType) {
    const labels = {
      vendor: 'Brand',
      productType: 'Product Type',
      collections: 'Collection',
      size: 'Size',
      color: 'Color',
      material: 'Material',
      style: 'Style',
      thickness: 'Thickness',
      wearLayer: 'Wear Layer',
      width: 'Width',
      length: 'Length'
    };
    return labels[filterType] || filterType;
  }

  removeActiveFilter(filterType, filterValue) {
    this.showCollectionPreloader('Updating filters...');
    
    if (filterType === 'priceRange') {
      this.currentFilters.priceRange.min = null;
      this.currentFilters.priceRange.max = null;
      // Reset price selects
      const minSelect = document.getElementById('min-price');
      const maxSelect = document.getElementById('max-price');
      if (minSelect) minSelect.value = '';
      if (maxSelect) maxSelect.value = '';
    } else if (Array.isArray(this.currentFilters[filterType])) {
      const index = this.currentFilters[filterType].indexOf(filterValue);
      if (index > -1) {
        this.currentFilters[filterType].splice(index, 1);
        
        // Reset the corresponding UI element
        const checkbox = document.querySelector(`.filter-checkbox[data-filter-type="${filterType}"][value="${filterValue}"]`);
        if (checkbox) {
          checkbox.checked = false;
        }
        
        const select = document.querySelector(`.filter-select[data-filter-type="${filterType}"]`);
        if (select && select.value === filterValue) {
          select.value = '';
        }
      }
    }

    this.ensureCurrentSort();
    this.currentPage = 1;
    
    setTimeout(() => {
      this.applyFiltersAndSort();
      this.updateDisplay();
      this.updateActiveFiltersDisplay();
      this.updateURL();
      this.hideCollectionPreloader();
    }, 1000);
  }

  clearAllFilters() {
    this.showCollectionPreloader('Clearing all filters...');
    
    this.currentFilters = {
      priceRange: { min: null, max: null },
      productType: [],
      size: [],
      material: [],
      style: [],
      thickness: [],
      wearLayer: [],
      width: [],
      length: [],
      availability: null
    };

    // Clear UI elements
    const filterCheckboxes = document.querySelectorAll('.filter-checkbox');
    filterCheckboxes.forEach(checkbox => checkbox.checked = false);

    const filterSelects = document.querySelectorAll('.filter-select');
    filterSelects.forEach(select => select.value = '');

    const priceSelects = document.querySelectorAll('#min-price, #max-price');
    priceSelects.forEach(select => select.value = '');

    // Clear sort as well
    this.currentSort = '';
    const sortDropdown = document.getElementById('sort-dropdown');
    if (sortDropdown) {
      sortDropdown.value = '';
    }

    this.currentPage = 1;
    
    setTimeout(() => {
      this.applyFiltersAndSort();
      this.updateDisplay();
      this.updateActiveFiltersDisplay();
      this.updateURL();
      this.hideCollectionPreloader();
    }, 1000);
  }

  sortProducts(products, sortKey) {
    const sortOption = this.sortOptions[sortKey];
    if (!sortOption || !sortOption.field) return products;

    return products.sort((a, b) => {
      const aValue = this.getSortValue(a, sortOption.field);
      const bValue = this.getSortValue(b, sortOption.field);

      let comparison = 0;

      // Handle different data types
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        comparison = aValue.toLowerCase().localeCompare(bValue.toLowerCase());
      } else if (typeof aValue === 'number' && typeof bValue === 'number') {
        comparison = aValue - bValue;
      } else if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
        comparison = bValue - aValue; // true values first
      } else {
        // Convert to strings for comparison
        comparison = String(aValue).toLowerCase().localeCompare(String(bValue).toLowerCase());
      }

      return sortOption.direction === 'desc' ? -comparison : comparison;
    });
  }

  getSortValue(product, field) {
    switch (field) {
      case 'displayPrice':
        return product.displayPrice;
      case 'sortPrice':
        return product.sortPrice;
      case 'title':
        return product.title;
      case 'vendor':
        return product.vendor;
      case 'productType':
        return product.productType;
      case 'availability':
        return product.availability;
      case 'createdAt':
        return new Date(product.createdAt);
      case 'size':
        return product.size;
      case 'color':
        return product.color;
      case 'material':
        return product.material;
      case 'brand':
        return product.brand;
      default:
        return '';
    }
  }

  calculatePagination() {
    this.totalPages = Math.ceil(this.filteredProducts.length / this.itemsPerPage);
    if (this.currentPage > this.totalPages) {
      this.currentPage = Math.max(1, this.totalPages);
    }
  }

  updateDisplay() {
    this.calculatePagination();
    this.showCurrentPageProducts();
    this.updatePagination();
    this.updateProductCount();
    this.updateToolbarItemsPerPage();
  }

  updateToolbarItemsPerPage() {
    const toolbarSelect = document.getElementById('toolbar-items-per-page');
    if (toolbarSelect) {
      toolbarSelect.value = this.itemsPerPage.toString();
    }
  }

  showCurrentPageProducts() {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    const currentPageProducts = this.filteredProducts.slice(startIndex, endIndex);

    // Get the product grid container
    const productGrid = document.getElementById('product-grid');
    if (!productGrid) return;

    // Hide all products first
    this.products.forEach(product => {
      product.element.style.display = 'none';
    });

    // Clear the grid and re-append products in the correct order
    // This ensures the visual order matches the sorted order
    currentPageProducts.forEach((product, index) => {
      product.element.style.display = 'block';
      // Ensure the element is in the correct position in the DOM
      productGrid.appendChild(product.element);
    });

    // Reinitialize slick carousels for visible products
    this.reinitializeSlickCarousels();
  }

  // Method to reinitialize slick carousels on visible products
  reinitializeSlickCarousels() {
    // Wait a bit for DOM to settle
    setTimeout(() => {
      try {
        // Check if jQuery is available
        if (typeof $ === 'undefined' || typeof $.fn.slick === 'undefined') {
          return;
        }

        // Find all slick carousels within visible products
        const visibleProducts = document.querySelectorAll('.product-item[style*="block"]');
        
        visibleProducts.forEach(productElement => {
          const sliders = productElement.querySelectorAll('.slider-slick');
          
          sliders.forEach(slider => {
            const $slider = $(slider);
            
            // Destroy existing slick instance if it exists
            if ($slider.hasClass('slick-initialized')) {
              try {
                $slider.slick('destroy');
              } catch (e) {
                // Silent fail for destroy
              }
            }
            
            // Reinitialize the slider
            try {
              $slider.slick({
                infinite: true,
                slidesToShow: 1,
                slidesToScroll: 1,
                lazyLoad: 'ondemand',
                useTransform: true,
                useCSS: true,
                fade: false,
                speed: 300,
                cssEase: 'ease-in-out',
                prevArrow: `<svg class="slick-prev slick-arrow" xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 512 512"><path fill="currentColor" d="M512 256A256 256 0 1 0 0 256a256 256 0 1 0 512 0zM271 135c9.4-9.4 24.6-9.4 33.9 0s9.4 24.6 0 33.9l-87 87 87 87c9.4 9.4 9.4 24.6 0 33.9s-24.6 9.4-33.9 0L167 273c-9.4-9.4-9.4-24.6 0-33.9L271 135z"/></svg>`,
                nextArrow: `<svg class="slick-next slick-arrow" xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24"><path fill-rule="evenodd" clip-rule="evenodd" d="M12 0C18.6274 0 24 5.37258 24 12C24 18.6274 18.6274 24 12 24C5.37258 24 0 18.6274 0 12C0 5.37258 5.37258 0 12 0ZM10.6828 8.13017C10.5266 7.95661 10.2734 7.95661 10.1172 8.13017C9.96095 8.30374 9.96095 8.58515 10.1172 8.75871L13.0343 12L10.1172 15.2413C9.96095 15.4149 9.96095 15.6963 10.1172 15.8698C10.2734 16.0434 10.5266 16.0434 10.6828 15.8698L13.8828 12.3143C14.0391 12.1407 14.0391 11.8593 13.8828 11.6857L10.6828 8.13017Z" fill="currentColor"></path></svg>`
              });
            } catch (e) {
              // Silent fail for reinitialization
            }
          });
        });
        
        // Also initialize any sliders that might not be in products
        const allSliders = document.querySelectorAll('.slider-slick:not(.slick-initialized)');
        allSliders.forEach(slider => {
          const $slider = $(slider);
          try {
            $slider.slick({
              infinite: true,
              slidesToShow: 1,
              slidesToScroll: 1,
              lazyLoad: 'ondemand',
              useTransform: true,
              useCSS: true,
              fade: false,
              speed: 300,
              cssEase: 'ease-in-out',
              prevArrow: `<svg class="slick-prev slick-arrow" xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 512 512"><path fill="currentColor" d="M512 256A256 256 0 1 0 0 256a256 256 0 1 0 512 0zM271 135c9.4-9.4 24.6-9.4 33.9 0s9.4 24.6 0 33.9l-87 87 87 87c9.4 9.4 9.4 24.6 0 33.9s-24.6 9.4-33.9 0L167 273c-9.4-9.4-9.4-24.6 0-33.9L271 135z"/></svg>`,
              nextArrow: `<svg class="slick-next slick-arrow" xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24"><path fill-rule="evenodd" clip-rule="evenodd" d="M12 0C18.6274 0 24 5.37258 24 12C24 18.6274 18.6274 24 12 24C5.37258 24 0 18.6274 0 12C0 5.37258 5.37258 0 12 0ZM10.6828 8.13017C10.5266 7.95661 10.2734 7.95661 10.1172 8.13017C9.96095 8.30374 9.96095 8.58515 10.1172 8.75871L13.0343 12L10.1172 15.2413C9.96095 15.4149 9.96095 15.6963 10.1172 15.8698C10.2734 16.0434 10.5266 16.0434 10.6828 15.8698L13.8828 12.3143C14.0391 12.1407 14.0391 11.8593 13.8828 11.6857L10.6828 8.13017Z" fill="currentColor"></path></svg>`
            });
          } catch (e) {
            // Silent fail for reinitialization
          }
        });
      } catch (error) {
        // Silent fail for reinitialization
      }
    }, 100); // Small delay to ensure DOM is ready
  }

  updatePagination() {
    const container = document.getElementById('pagination-container');
    if (!container) return;

    container.innerHTML = `${this.generatePaginationControls()}`;
  }

  generatePaginationControls() {
    if (this.totalPages <= 1) return '';

    let controls = '<div class="pagination-controls">';
    
    // Previous button
    controls += `
      <button class="pagination-btn" data-page="${this.currentPage - 1}" 
              ${this.currentPage === 1 ? 'disabled' : ''}>
        Previous
      </button>
    `;

    // Page numbers
    const startPage = Math.max(1, this.currentPage - 2);
    const endPage = Math.min(this.totalPages, this.currentPage + 2);

    if (startPage > 1) {
      controls += `<button class="pagination-btn" data-page="1">1</button>`;
      if (startPage > 2) {
        controls += `<span class="pagination-ellipsis">…</span>`;
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      controls += `
        <button class="pagination-btn ${i === this.currentPage ? 'active' : ''}" 
                data-page="${i}">
          ${i}
        </button>
      `;
    }

    if (endPage < this.totalPages) {
      if (endPage < this.totalPages - 1) {
        controls += `<span class="pagination-ellipsis">…</span>`;
      }
      controls += `<button class="pagination-btn" data-page="${this.totalPages}">${this.totalPages}</button>`;
    }

    // Next button
    controls += `
      <button class="pagination-btn" data-page="${this.currentPage + 1}" 
              ${this.currentPage === this.totalPages ? 'disabled' : ''}>
        Next
      </button>
    `;

    controls += '</div>';
    return controls;
  }

  updateProductCount() {
    const countElements = document.querySelectorAll('#ProductCountDesktop, .product-count');
    countElements.forEach(element => {
      element.textContent = `${this.filteredProducts.length} products`;
    });
  }

  parseURLParams() {
    const params = new URLSearchParams(window.location.search);
    
    // Parse sort
    this.currentSort = params.get('sort') || '';
    
    // Parse page
    this.currentPage = parseInt(params.get('page')) || 1;
    
    // Parse items per page
    this.itemsPerPage = parseInt(params.get('per_page')) || 24;
    
    // Parse filters
    this.parseFiltersFromURL(params);
    
    // Update UI
    const sortDropdown = document.getElementById('sort-dropdown');
    if (sortDropdown) {
      sortDropdown.value = this.currentSort;
    }
    
    this.updateFilterUI();
    this.applyFiltersAndSort();
    this.updateDisplay();
  }

  parseFiltersFromURL(params) {
    // Reset filters
    this.currentFilters = {
      priceRange: { min: null, max: null },
      productType: [],
      size: [],
      material: [],
      style: [],
      thickness: [],
      wearLayer: [],
      width: [],
      length: [],
      availability: null
    };

    // Parse price range
    const minPrice = params.get('price_min');
    const maxPrice = params.get('price_max');
    if (minPrice) this.currentFilters.priceRange.min = parseFloat(minPrice);
    if (maxPrice) this.currentFilters.priceRange.max = parseFloat(maxPrice);

    // Parse other filters
    params.forEach((value, key) => {
      if (key.startsWith('filter_')) {
        const filterName = key.replace('filter_', '');
        if (this.currentFilters[filterName] && Array.isArray(this.currentFilters[filterName])) {
          this.currentFilters[filterName] = value.split(',');
        }
      }
    });
  }

  updateFilterUI() {
    // Update price selects
    const minPriceSelect = document.getElementById('min-price');
    const maxPriceSelect = document.getElementById('max-price');
    
    if (minPriceSelect && this.currentFilters.priceRange.min !== null) {
      minPriceSelect.value = this.currentFilters.priceRange.min;
    }
    if (maxPriceSelect && this.currentFilters.priceRange.max !== null) {
      maxPriceSelect.value = this.currentFilters.priceRange.max;
    }

    // Update checkboxes and dropdowns
    Object.keys(this.currentFilters).forEach(filterType => {
      if (Array.isArray(this.currentFilters[filterType])) {
        this.currentFilters[filterType].forEach(value => {
          // Update checkboxes
          const checkbox = document.querySelector(`.filter-checkbox[data-filter-type="${filterType}"][value="${value}"]`);
          if (checkbox) checkbox.checked = true;
          
          // Update dropdowns
          const select = document.querySelector(`.filter-select[data-filter-type="${filterType}"]`);
          if (select) select.value = value;
        });
      }
    });

    this.updateActiveFiltersDisplay();
  }

  updateURL() {
    const params = new URLSearchParams();
    
    if (this.currentSort) {
      params.set('sort', this.currentSort);
    }
    
    if (this.currentPage > 1) {
      params.set('page', this.currentPage);
    }
    
    if (this.itemsPerPage !== 24) {
      params.set('per_page', this.itemsPerPage);
    }

    // Add filter parameters
    if (this.currentFilters.priceRange.min !== null) {
      params.set('price_min', this.currentFilters.priceRange.min);
    }
    if (this.currentFilters.priceRange.max !== null) {
      params.set('price_max', this.currentFilters.priceRange.max);
    }

    Object.keys(this.currentFilters).forEach(filterName => {
      if (Array.isArray(this.currentFilters[filterName]) && this.currentFilters[filterName].length > 0) {
        params.set(`filter_${filterName}`, this.currentFilters[filterName].join(','));
      }
    });
    
    const newURL = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
    window.history.replaceState({}, '', newURL);
  }

  scrollToTop() {
    const productGrid = document.getElementById('ProductGridContainer');
    if (productGrid) {
      productGrid.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }
}

// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', function() {
  new CollectionProductGrid();
}); 